import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  Quiz as QuizIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import {
  useGetPublicMockTestStatsQuery,
  useGetAdminExamsQuery,
} from "@/store/api/adminApi";

const fmtScore = (v) => (v === null || v === undefined ? "—" : v);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const SummaryCard = ({ icon, label, value, color }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}1a`,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const OverviewView = ({ data }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6} md={3}>
      <SummaryCard
        icon={<AssessmentIcon />}
        label="Total attempts"
        value={data?.totalAttempts ?? 0}
        color="#1976d2"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <SummaryCard
        icon={<QuizIcon />}
        label="Full attempts"
        value={data?.fullAttempts ?? 0}
        color="#9c27b0"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <SummaryCard
        icon={<QuizIcon />}
        label="Part attempts"
        value={data?.partAttempts ?? 0}
        color="#0288d1"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <SummaryCard
        icon={<TrophyIcon />}
        label="Average score (full)"
        value={fmtScore(data?.averageScore)}
        color="#2e7d32"
      />
    </Grid>
  </Grid>
);

const ByExamView = ({ rows }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Test</TableCell>
          <TableCell align="right">Total</TableCell>
          <TableCell align="right">Full</TableCell>
          <TableCell align="right">Part</TableCell>
          <TableCell align="right">Avg score</TableCell>
          <TableCell align="right">Best score</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.examId}>
            <TableCell>{r.examTitle}</TableCell>
            <TableCell align="right">{r.totalAttempts ?? 0}</TableCell>
            <TableCell align="right">{r.fullAttempts ?? 0}</TableCell>
            <TableCell align="right">{r.partAttempts ?? 0}</TableCell>
            <TableCell align="right">{fmtScore(r.averageScore)}</TableCell>
            <TableCell align="right">{fmtScore(r.bestScore)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ByUserView = ({ rows }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>User</TableCell>
          <TableCell>Email</TableCell>
          <TableCell align="right">Total</TableCell>
          <TableCell align="right">Avg score</TableCell>
          <TableCell align="right">Best score</TableCell>
          <TableCell align="right">Last completed</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.userId}>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                {r.userName}
              </Box>
            </TableCell>
            <TableCell>{r.userEmail}</TableCell>
            <TableCell align="right">{r.totalAttempts ?? 0}</TableCell>
            <TableCell align="right">{fmtScore(r.averageScore)}</TableCell>
            <TableCell align="right">{fmtScore(r.bestScore)}</TableCell>
            <TableCell align="right">{fmtDate(r.lastCompletedAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/**
 * Admin analytics for public mock tests. Self-contained: owns its own `type`
 * switch (overview | by-exam | by-user) and optional exam filter, and fetches
 * via `useGetPublicMockTestStatsQuery`. No class selection involved.
 */
const PublicMockTestReport = () => {
  const [type, setType] = useState("overview");
  const [selectedExam, setSelectedExam] = useState(null);

  // Exam list for the optional filter (only relevant for by-exam / by-user).
  const { data: examsData } = useGetAdminExamsQuery({ page: 1, limit: 100 });
  const examOptions = useMemo(
    () => examsData?.data?.exams || examsData?.data || [],
    [examsData]
  );

  const examId = type === "overview" ? undefined : selectedExam?._id;

  const { data, isFetching, isError, error } = useGetPublicMockTestStatsQuery({
    type,
    examId,
  });

  const rows = Array.isArray(data) ? data : [];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={type}
          exclusive
          onChange={(_, v) => v && setType(v)}
          size="small"
        >
          <ToggleButton value="overview">Overview</ToggleButton>
          <ToggleButton value="by-exam">By exam</ToggleButton>
          <ToggleButton value="by-user">By user</ToggleButton>
        </ToggleButtonGroup>

        {type !== "overview" && (
          <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={examOptions}
            value={selectedExam}
            onChange={(_, v) => setSelectedExam(v)}
            getOptionLabel={(o) => o?.title || ""}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            renderInput={(params) => (
              <TextField {...params} label="Filter by exam (optional)" />
            )}
          />
        )}
      </Box>

      {isFetching && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert severity="error">
          {error?.data?.message || "Failed to load public mock test stats."}
        </Alert>
      )}

      {!isFetching && !isError && (
        <>
          {type === "overview" && <OverviewView data={data} />}
          {type === "by-exam" &&
            (rows.length === 0 ? (
              <Alert severity="info">No attempts found.</Alert>
            ) : (
              <ByExamView rows={rows} />
            ))}
          {type === "by-user" &&
            (rows.length === 0 ? (
              <Alert severity="info">No attempts found.</Alert>
            ) : (
              <ByUserView rows={rows} />
            ))}
        </>
      )}
    </Box>
  );
};

export default PublicMockTestReport;
